import { useState, useEffect } from 'react';
import { builderApi } from '../api';

const ROLES = ['Admin', 'Supervisor', 'Civil Engineer', 'Mechanical Engineer', 'Electrical Engineer', 'Site Engineer', 'Accountant', 'Store Keeper'];
const TRADES = ['', 'Plumber', 'Electrician', 'Carpenter', 'Painter', 'Mason', 'Welder', 'Fabricator', 'Excavation Operator', 'Tiler', 'Waterproofing', 'HVAC Technician'];
const EMPTY = { name: '', phone: '', email: '', emp_role: 'Supervisor', trade: '' };

const ErrBanner = ({ msg, onClose }) => msg ? (
  <div style={{ background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 8,
    padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
    <span style={{ fontSize: 18 }}>⚠️</span>
    <span style={{ color: '#c53030', fontSize: 13, fontWeight: 600, flex: 1 }}>{msg}</span>
    <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#c53030', cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
  </div>
) : null;

const inv = (fe, k) => fe[k] ? { borderColor: '#e53e3e', background: '#fff5f5', boxShadow: '0 0 0 3px rgba(229,62,62,.15)' } : {};

export default function BuilderEmployees({ user }) {
  const isOwner = user?.role === 'role_owner';
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [fe, setFe] = useState({});
  const [search, setSearch] = useState('');

  const load = () => builderApi.getEmployees().then(setEmployees).catch(e => setErr(e.message)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const errors = {};
    if (!form.name.trim()) errors.name = true;
    if (!form.emp_role) errors.emp_role = true;
    setFe(errors);
    if (Object.keys(errors).length > 0) { setErr('Please fill in all required fields (marked *)'); return; }
    try { await builderApi.createEmployee(form); setShowForm(false); setForm(EMPTY); setFe({}); load(); }
    catch (e) { setErr(e.message); }
  };

  const del = async (id) => {
    if (!window.confirm('Remove this employee?')) return;
    try { await builderApi.deleteEmployee(id); load(); } catch (e) { setErr(e.message); }
  };

  const filtered = employees.filter(e =>
    (e.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (e.emp_role || '').toLowerCase().includes(search.toLowerCase()) ||
    (e.trade || '').toLowerCase().includes(search.toLowerCase())
  );

  const f = (k, v) => { setForm(prev => ({ ...prev, [k]: v })); if (fe[k]) setFe(p => ({ ...p, [k]: false })); };

  return (
    <div className="bld-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, color: 'var(--text-primary)' }}>Employees</h2>
        <button className="btn btn-primary" onClick={() => { setShowForm(s => !s); setFe({}); setErr(''); }}>+ Add Employee</button>
      </div>

      <ErrBanner msg={err} onClose={() => setErr('')} />

      {showForm && (
        <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Full Name *</label>
              <input className="form-input" style={inv(fe,'name')} value={form.name}
                onChange={e => f('name', e.target.value)} placeholder="Employee full name" />
              {fe.name && <div style={{ color: '#e53e3e', fontSize: 11, marginTop: 3 }}>Required</div>}
            </div>
            <div>
              <label className="form-label">Phone</label>
              <input className="form-input" type="tel" value={form.phone} onChange={e => f('phone', e.target.value)} placeholder="Mobile number" />
            </div>
            <div>
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email} onChange={e => f('email', e.target.value)} placeholder="Email address" />
            </div>
            <div>
              <label className="form-label">Role *</label>
              <select className="form-input" style={inv(fe,'emp_role')} value={form.emp_role} onChange={e => f('emp_role', e.target.value)}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              {fe.emp_role && <div style={{ color: '#e53e3e', fontSize: 11, marginTop: 3 }}>Required</div>}
            </div>
            <div>
              <label className="form-label">Trade / Specialisation</label>
              <select className="form-input" value={form.trade} onChange={e => f('trade', e.target.value)}>
                {TRADES.map(t => <option key={t} value={t}>{t || '(none)'}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-primary" onClick={save}>Add Employee</button>
            <button className="btn btn-outline" onClick={() => { setShowForm(false); setFe({}); setErr(''); }}>Cancel</button>
          </div>
        </div>
      )}

      <input className="form-input" placeholder="Search by name, role or trade…" value={search}
        onChange={e => setSearch(e.target.value)} style={{ maxWidth: 320, marginBottom: 12 }} />

      {loading ? <div style={{ color: '#718096', fontSize: 13 }}>Loading…</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr><th>#</th><th>Name</th><th>Role</th><th>Trade</th><th>Phone</th><th>Email</th><th>Action</th></tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: '#718096', padding: 24 }}>
                  {search ? 'No results found' : 'No employees added yet'}
                </td></tr>
              )}
              {filtered.map((emp, idx) => (
                <tr key={emp.id}>
                  <td style={{ color: '#718096', fontSize: 12 }}>{idx + 1}</td>
                  <td style={{ fontWeight: 600 }}>{emp.name}</td>
                  <td>
                    <span style={{ background: '#ebf8ff', color: '#2b6cb0', padding: '2px 8px', borderRadius: 10, fontSize: 11 }}>
                      {emp.emp_role}
                    </span>
                  </td>
                  <td>{emp.trade || <span style={{ color: '#718096' }}>—</span>}</td>
                  <td>{emp.phone || '—'}</td>
                  <td style={{ fontSize: 12 }}>{emp.email || '—'}</td>
                  <td>
                    {isOwner && (
                      <button className="btn" style={{ background: '#fed7d7', color: '#c53030', padding: '3px 10px', fontSize: 12 }} onClick={() => del(emp.id)}>Remove</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
